class LensFinder extends HTMLElement {
  constructor() {
    super();

    this.currentStep = 1;
    this.totalSteps = 3;
    this.stepElements = [];
    this.isSubmitting = false;
    this.hasTrackedStart = false;
    this.pendingStartTracking = false;

    this.handleActionClick = this.handleActionClick.bind(this);
    this.handleChoiceChange = this.handleChoiceChange.bind(this);
    this.handleAnalyticsReady = this.handleAnalyticsReady.bind(this);
  }

  connectedCallback() {
    this.stepElements = Array.from(
      this.querySelectorAll('[data-lens-finder-step]'),
    );
    this.totalSteps = this.stepElements.length
      ? this.stepElements.length
      : Number.parseInt(this.dataset.totalSteps || '0', 10);

    this.progressCurrentElement = this.querySelector(
      '[data-lens-finder-progress-current]',
    );
    this.progressTotalElement = this.querySelector(
      '[data-lens-finder-progress-total]',
    );
    this.progressFillElement = this.querySelector(
      '[data-lens-finder-progress-fill]',
    );
    this.errorElement = this.querySelector('[data-lens-finder-error]');
    this.stepsWrapperElement = this.querySelector(
      '[data-lens-finder-steps-wrapper]',
    );
    this.questionsBoxElement = this.querySelector(
      '[data-lens-finder-questions-box]',
    );
    this.questionsElement = this.querySelector('[data-lens-finder-questions]');
    this.ajaxResultsElement = this.querySelector(
      '[data-lens-finder-ajax-results]',
    );
    this.resultsContentElement = this.querySelector(
      '[data-lens-finder-results-content]',
    );
    this.summaryElement = this.querySelector('[data-lens-finder-summary]');
    this.summaryHeaderElement = this.querySelector(
      '[data-lens-finder-summary-header]',
    );
    this.spinnerElement = this.querySelector('[data-lens-finder-spinner]');
    this.refreshSpinnerElement = this.querySelector(
      '[data-lens-finder-refresh-spinner]',
    );
    this.noResultsElement = this.querySelector('[data-lens-finder-no-results]');
    this.skinColorsElement = this.querySelector(
      '[data-lens-finder-skin-colors]',
    );
    this.lensColorsElement = this.querySelector('[data-lens-colors]');
    this.repeatButtonWrapper = this.querySelector(
      '[data-lens-finder-repeat-wrapper]',
    );
    this.lensColorGroupElements = Array.from(
      this.querySelectorAll('[data-lens-colors-group]'),
    );
    this.backButtonWrapper = this.querySelector(
      '[data-lens-finder-back-wrapper]',
    );
    this.nextButtonWrapper = this.querySelector(
      '[data-lens-finder-next-wrapper]',
    );
    this.submitButtonWrapper = this.querySelector(
      '[data-lens-finder-submit-wrapper]',
    );

    if (this.progressTotalElement) {
      this.progressTotalElement.textContent = String(this.totalSteps);
    }

    this.addEventListener('click', this.handleActionClick);
    this.addEventListener('change', this.handleChoiceChange);
    document.addEventListener('lensza:analytics-ready', this.handleAnalyticsReady);

    const checkedSkinColorChoice = this.querySelector(
      '[data-lens-finder-skin-color-choice]:checked',
    );

    if (checkedSkinColorChoice instanceof HTMLInputElement) {
      this.updateLensColorsVisibility(checkedSkinColorChoice.value);
    } else {
      this.hideLensColors();
    }

    this.updateStepUI();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleActionClick);
    this.removeEventListener('change', this.handleChoiceChange);
    document.removeEventListener(
      'lensza:analytics-ready',
      this.handleAnalyticsReady,
    );
  }

  handleActionClick(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const actionTrigger = event.target.closest('[data-lens-finder-action]');

    if (!actionTrigger || !this.contains(actionTrigger)) {
      return;
    }

    const action = actionTrigger.dataset.lensFinderAction;

    if (!action) {
      return;
    }

    if (action === 'back') {
      this.goToStep(this.currentStep - 1);
      return;
    }

    if (action === 'repeat') {
      this.resetFinder();
      return;
    }

    if (!this.validateCurrentStep()) {
      return;
    }

    if (action === 'next') {
      this.track('lens_finder_step_completed', {
        step_number: this.currentStep,
      });
      this.goToStep(this.currentStep + 1);
      return;
    }

    if (action === 'submit') {
      this.submitFinder();
    }
  }

  handleChoiceChange(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const selectedFinderChoice = event.target.closest(
      '[data-lens-finder-choice], [data-lens-finder-skin-color-choice], [data-lens-finder-lens-color-choice]',
    );

    if (selectedFinderChoice && this.contains(selectedFinderChoice)) {
      this.trackStarted();
    }

    const lensColorChoice = event.target.closest(
      '[data-lens-finder-lens-color-choice]',
    );

    if (lensColorChoice && this.contains(lensColorChoice)) {
      this.hideError();
      if (!this.isSubmitting) {
        this.submitFinder({ scrollTarget: this.resultsContentElement });
      }
      return;
    }

    if (event.target instanceof HTMLSelectElement) {
      const lensColorGroup = event.target.closest('[data-lens-colors-group]');

      if (lensColorGroup && this.contains(lensColorGroup)) {
        this.syncLensColorSelectToRadio(lensColorGroup, event.target.value);
        return;
      }
    }

    const changedChoice = event.target.closest('[data-lens-finder-choice]');

    if (!changedChoice || !this.contains(changedChoice)) {
      const skinColorChoice = event.target.closest(
        '[data-lens-finder-skin-color-choice]',
      );

      if (!skinColorChoice || !this.contains(skinColorChoice)) {
        return;
      }

      this.hideError();
      this.updateLensColorsVisibility(skinColorChoice.value);
      if (!this.isSubmitting) {
        this.submitFinder({ scrollTarget: this.resultsContentElement });
      }
      return;
    }

    this.hideError();
  }

  goToStep(nextStep) {
    const boundedStep = Math.max(1, Math.min(nextStep, this.totalSteps));

    if (boundedStep === this.currentStep) {
      return;
    }

    this.currentStep = boundedStep;
    this.hideError();
    this.updateStepUI();
  }

  updateStepUI() {
    this.stepElements.forEach((stepElement, index) => {
      const isActive = index + 1 === this.currentStep;

      stepElement.classList.toggle('hidden', !isActive);
      stepElement.setAttribute('aria-hidden', String(!isActive));
    });

    if (this.progressCurrentElement) {
      this.progressCurrentElement.textContent = String(
        this.totalSteps > 0 ? this.currentStep : 0,
      );
    }

    if (this.progressFillElement) {
      const denominator = Math.max(this.totalSteps, 1);
      const progressPercent = (this.currentStep / denominator) * 100;
      this.progressFillElement.style.setProperty(
        '--lens-finder-progress',
        `${progressPercent}%`,
      );
    }

    if (this.totalSteps === 0) {
      this.backButtonWrapper?.classList.add('hidden');
      this.nextButtonWrapper?.classList.add('hidden');
      this.submitButtonWrapper?.classList.add('hidden');
      return;
    }

    const isFirstStep = this.currentStep === 1;
    const isLastStep = this.currentStep === this.totalSteps;

    this.backButtonWrapper?.classList.toggle('hidden', isFirstStep);
    this.nextButtonWrapper?.classList.toggle('hidden', isLastStep);
    this.submitButtonWrapper?.classList.toggle('hidden', !isLastStep);
  }

  validateCurrentStep() {
    const currentStepElement = this.getStepElementByIndex(this.currentStep);

    if (!currentStepElement) {
      return false;
    }

    const hasSelection = Boolean(
      currentStepElement.querySelector(
        'input[data-lens-finder-choice]:checked',
      ),
    );

    if (hasSelection) {
      this.hideError();
      return true;
    }

    this.showError(
      this.dataset.validationMessage ||
        'Please select one option before continuing.',
    );
    return false;
  }

  buildPayload() {
    const steps = this.stepElements.map((stepElement, index) => {
      const fieldKey =
        stepElement.dataset.lensFinderFieldKey || `step_${index + 1}`;
      const filterParam = stepElement.dataset.lensFinderFilterParam || '';
      const selectedInput = stepElement.querySelector(
        'input[data-lens-finder-choice]:checked',
      );

      if (!(selectedInput instanceof HTMLInputElement)) {
        return {
          fieldKey,
          filterParam,
          value: null,
          label: null,
          filterValue: null,
        };
      }

      return {
        fieldKey,
        filterParam,
        value: selectedInput.value,
        label: this.getChoiceLabel(selectedInput),
        filterValue:
          selectedInput.dataset.lensFinderChoiceFilterValue ||
          this.getChoiceLabel(selectedInput),
      };
    });

    const fields = {};

    steps.forEach((step) => {
      fields[step.fieldKey] = {
        value: step.value,
        label: step.label,
        filterValue: step.filterValue,
        filterParam: step.filterParam,
      };
    });

    const selectedLensColorInput = this.querySelector(
      'input[data-lens-finder-lens-color-choice]:checked',
    );
    const lensColor = this.getLensColorSelection(selectedLensColorInput);

    return {
      steps,
      fields,
      lensColor,
    };
  }

  getChoiceLabel(inputElement) {
    const explicitLabel = inputElement.dataset.lensFinderChoiceLabel?.trim();

    if (explicitLabel) {
      return explicitLabel;
    }

    const optionCard = inputElement.nextElementSibling;
    const labelElement = optionCard?.querySelector('.h6');

    if (!labelElement) {
      return inputElement.value;
    }

    return labelElement.textContent?.trim() || inputElement.value;
  }

  async submitFinder(options = {}) {
    const { scrollTarget = null } = options;

    if (this.isSubmitting) {
      return;
    }

    const payload = this.buildPayload();
    const hasAllSelections = payload.steps.every((step) => step.label);

    if (!hasAllSelections) {
      this.showError(
        this.dataset.validationMessage ||
          'Please select one option before continuing.',
      );
      return;
    }

    this.isSubmitting = true;
    this.hideError();
    this.populateSummary(payload);
    this.lockHeight();
    this.scrollToSelf(scrollTarget);
    this.showResultsLoading();

    try {
      const queryString = this.buildResultsQuery(payload);
      const response = await fetch(`/collections/all?${queryString}`, {
        method: 'GET',
        headers: {
          Accept: 'text/html',
        },
      });

      if (!response.ok) {
        throw new Error(`Lens finder request failed: ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const sectionContent = html.getElementById(
        'shopify-section-collection-product-cards',
      );
      const sectionMarkup = sectionContent
        ? sectionContent.innerHTML
        : html.body?.innerHTML?.trim() || '';
      const hasResults = sectionContent
        ? sectionContent.querySelector('product-card') !== null
        : false;

      if (!sectionMarkup || !this.resultsContentElement || !hasResults) {
        this.track('lens_finder_completed', { recommendation_count: 0 });
        this.showNoResults(
          this.dataset.noResultsMessage || 'No products matched your answers.',
        );
        this.updateSkinColorsVisibility(payload);
        return;
      }

      this.spinnerElement?.classList.add('hidden');
      this.spinnerElement?.classList.remove('flex');
      this.hideNoResults();
      this.resultsContentElement.classList.remove('hidden');
      this.resultsContentElement.innerHTML = sectionMarkup;
      this.track('lens_finder_completed', {
        recommendation_count: this.resultsContentElement.querySelectorAll(
          'product-card',
        ).length,
      });
      this.summaryHeaderElement?.classList.remove('hidden');
      this.animateSummary();
      this.showResults();
      this.updateSkinColorsVisibility(payload);
      this.releaseHeight();
    } catch (_error) {
      this.showResultsError(
        this.dataset.fetchErrorMessage ||
          'We could not load recommendations. Please try again.',
      );
    } finally {
      this.isSubmitting = false;
    }
  }

  buildResultsQuery(payload) {
    const queryParams = new URLSearchParams();

    queryParams.set('sort_by', 'best-selling');
    queryParams.set('section_id', 'collection-product-cards');
    payload.steps.forEach((step) => {
      if (!step.filterParam || !step.filterValue) {
        return;
      }

      const filterKey = step.filterParam.startsWith('filter.')
        ? step.filterParam
        : `filter.p.m.custom.${step.filterParam}`;

      step.filterValue
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean)
        .forEach((filterValue) => {
          queryParams.append(filterKey, filterValue);
        });
    });

    payload.lensColor.filterValues.forEach((filterValue) => {
      queryParams.append('filter.p.m.custom.colors', filterValue);
    });

    if (payload.lensColor.softlensType) {
      queryParams.append(
        'filter.p.m.custom.softlens_type',
        payload.lensColor.softlensType,
      );
    }

    return queryParams.toString();
  }

  getLensColorSelection(inputElement) {
    if (!(inputElement instanceof HTMLInputElement)) {
      return {
        value: null,
        label: null,
        filterValues: [],
        softlensType: '',
      };
    }

    const label =
      inputElement.dataset.lensFinderLensColorLabel?.trim() ||
      inputElement.value;

    if (inputElement.dataset.lensFinderLensColorDefault === 'true') {
      return {
        value: inputElement.value,
        label,
        filterValues: [],
        softlensType: '',
      };
    }

    const rawFilterValues =
      inputElement.dataset.lensFinderLensColorFilterValues?.trim() || '';
    const rawSoftlensType =
      inputElement.dataset.lensFinderLensColorSoftlensType?.trim() || '';

    return {
      value: inputElement.value,
      label,
      filterValues: rawFilterValues
        ? rawFilterValues
            .split(',')
            .map((filterValue) => filterValue.trim())
            .filter(Boolean)
        : [],
      softlensType: rawSoftlensType,
    };
  }

  showResultsLoading() {
    const isRefreshing =
      this.ajaxResultsElement?.classList.contains('hidden') === false;

    if (this.questionsBoxElement) {
      this.questionsBoxElement.style.height = `${this.questionsBoxElement.offsetHeight}px`;
    }

    this.hideNoResults();

    if (this.resultsContentElement) {
      this.resultsContentElement.innerHTML = '';
      this.resultsContentElement.classList.add('hidden');
    }

    this.repeatButtonWrapper?.classList.toggle('hidden', isRefreshing);

    if (isRefreshing) {
      this.refreshSpinnerElement?.classList.remove('hidden');
      this.refreshSpinnerElement?.classList.add('flex');
    } else {
      this.spinnerElement?.classList.remove('hidden');
      this.spinnerElement?.classList.add('flex');
    }

    this.questionsElement?.classList.add('hidden');
    this.questionsElement?.setAttribute('aria-hidden', 'true');

    if (isRefreshing) {
      this.ajaxResultsElement?.classList.remove('hidden');
      this.ajaxResultsElement?.setAttribute('aria-hidden', 'false');
    } else {
      this.ajaxResultsElement?.classList.add('hidden');
      this.ajaxResultsElement?.setAttribute('aria-hidden', 'true');
    }
  }

  showResultsError(message) {
    this.spinnerElement?.classList.add('hidden');
    this.spinnerElement?.classList.remove('flex');
    this.refreshSpinnerElement?.classList.add('hidden');
    this.refreshSpinnerElement?.classList.remove('flex');
    this.repeatButtonWrapper?.classList.remove('hidden');
    this.hideNoResults();

    if (this.resultsContentElement) {
      this.resultsContentElement.classList.remove('hidden');
      this.resultsContentElement.textContent = message;
    }

    this.showResults();
    this.releaseHeight();
  }

  showNoResults(message) {
    this.spinnerElement?.classList.add('hidden');
    this.spinnerElement?.classList.remove('flex');
    this.refreshSpinnerElement?.classList.add('hidden');
    this.refreshSpinnerElement?.classList.remove('flex');
    this.repeatButtonWrapper?.classList.remove('hidden');

    if (this.resultsContentElement) {
      this.resultsContentElement.innerHTML = '';
      this.resultsContentElement.classList.add('hidden');
    }

    if (this.noResultsElement) {
      this.noResultsElement.textContent = message;
      this.noResultsElement.classList.remove('hidden');
    }

    this.summaryHeaderElement?.classList.remove('hidden');
    this.animateSummary();
    this.showResults();
    this.releaseHeight();
  }

  showResults() {
    this.spinnerElement?.classList.add('hidden');
    this.spinnerElement?.classList.remove('flex');
    this.refreshSpinnerElement?.classList.add('hidden');
    this.refreshSpinnerElement?.classList.remove('flex');
    this.questionsElement?.classList.add('hidden');
    this.questionsElement?.setAttribute('aria-hidden', 'true');
    this.ajaxResultsElement?.classList.remove('hidden');
    this.ajaxResultsElement?.setAttribute('aria-hidden', 'false');
    this.repeatButtonWrapper?.classList.remove('hidden');
    this.stepsWrapperElement?.classList.add('hidden');
    this.stepsWrapperElement?.setAttribute('aria-hidden', 'true');

    if (this.questionsBoxElement) {
      this.questionsBoxElement.style.height = '';
    }
  }

  showQuestions() {
    this.ajaxResultsElement?.classList.add('hidden');
    this.ajaxResultsElement?.setAttribute('aria-hidden', 'true');
    this.questionsElement?.classList.remove('hidden');
    this.questionsElement?.setAttribute('aria-hidden', 'false');
    this.stepsWrapperElement?.classList.remove('hidden');
    this.stepsWrapperElement?.setAttribute('aria-hidden', 'false');
  }

  resetFinder() {
    const selectedInputs = this.querySelectorAll(
      'input[data-lens-finder-choice]:checked',
    );

    selectedInputs.forEach((inputElement) => {
      if (inputElement instanceof HTMLInputElement) {
        inputElement.checked = false;
      }
    });

    if (this.resultsContentElement) {
      this.resultsContentElement.innerHTML = '';
    }

    if (this.summaryElement) {
      this.summaryElement.innerHTML = '';
    }

    this.hideNoResults();
    this.hideSkinColors();
    this.hideLensColors();
    this.refreshSpinnerElement?.classList.add('hidden');
    this.refreshSpinnerElement?.classList.remove('flex');
    this.repeatButtonWrapper?.classList.remove('hidden');

    this.summaryHeaderElement?.classList.add('hidden');
    this.spinnerElement?.classList.add('hidden');
    this.spinnerElement?.classList.remove('flex');

    this.currentStep = 1;
    this.isSubmitting = false;
    this.hideError();
    this.scrollToSelf();
    this.showQuestions();
    this.updateStepUI();
  }

  lockHeight() {
    this.style.minHeight = `${this.offsetHeight}px`;
  }

  releaseHeight() {
    this.style.minHeight = '';
  }

  scrollToSelf(targetElement) {
    const element = targetElement || this;
    const offset = 180;
    const top = element.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  }

  populateSummary(payload) {
    if (!this.summaryElement) {
      return;
    }

    this.summaryElement.innerHTML = '';

    const labels = payload.steps.map((step) => step.label).filter(Boolean);

    labels.forEach((label, index) => {
      if (index > 0) {
        const sep = document.createElement('span');
        sep.className = 'lens-finder__answer-sep';
        sep.setAttribute('aria-hidden', 'true');
        sep.textContent = '—';
        this.summaryElement.appendChild(sep);
      }

      const item = document.createElement('span');
      item.className = 'lens-finder__answer-item text-sm';
      item.textContent = label;
      this.summaryElement.appendChild(item);
    });
  }

  animateSummary() {
    const items = this.summaryElement?.querySelectorAll(
      '.lens-finder__answer-item, .lens-finder__answer-sep',
    );

    if (!items) {
      return;
    }

    items.forEach((item, index) => {
      item.style.animationDelay = `${index * 0.25}s`;

      if (item.classList.contains('lens-finder__answer-item')) {
        item.classList.add('lens-finder__answer-item--visible');
      } else {
        item.classList.add('lens-finder__answer-sep--visible');
      }
    });
  }

  showError(message) {
    if (!this.errorElement) {
      return;
    }

    this.errorElement.textContent = message;
    this.errorElement.classList.remove('hidden');
  }

  hideError() {
    if (!this.errorElement) {
      return;
    }

    this.errorElement.textContent = '';
    this.errorElement.classList.add('hidden');
  }

  hideNoResults() {
    if (!this.noResultsElement) {
      return;
    }

    this.noResultsElement.textContent = '';
    this.noResultsElement.classList.add('hidden');
  }

  updateSkinColorsVisibility(payload) {
    if (!this.skinColorsElement) {
      return;
    }

    const targetStep = Number.parseInt(
      this.dataset.colorQuestionWhichStep || '',
      10,
    );
    const targetAnswerRaw = this.dataset.colorQuestionWhichAnswer || '';
    const targetAnswer = this.normalizeValue(targetAnswerRaw);

    if (!targetStep || !targetAnswer) {
      this.hideSkinColors();
      return;
    }

    const selectedStep = payload.steps[targetStep - 1];

    if (!selectedStep) {
      this.hideSkinColors();
      return;
    }

    const matchesAnswer = [
      selectedStep.value,
      selectedStep.label,
      selectedStep.filterValue,
    ]
      .map((value) => this.normalizeValue(value))
      .filter(Boolean)
      .includes(targetAnswer);

    this.skinColorsElement.classList.toggle('hidden', !matchesAnswer);
  }

  hideSkinColors() {
    this.skinColorsElement?.classList.add('hidden');
  }

  hideLensColors() {
    this.lensColorsElement?.classList.add('hidden');

    this.lensColorGroupElements.forEach((groupElement) => {
      groupElement.classList.add('hidden');
      groupElement.classList.remove('flex');
    });
  }

  updateLensColorsVisibility(selectedValue) {
    if (!this.lensColorGroupElements.length) {
      return;
    }

    const normalizedSelectedValue = this.normalizeValue(selectedValue);
    let hasVisibleGroup = false;

    this.lensColorGroupElements.forEach((groupElement) => {
      const normalizedGroupValue = this.normalizeValue(
        groupElement.dataset.lensColorsGroup,
      );
      const shouldShow =
        Boolean(normalizedSelectedValue) &&
        normalizedGroupValue === normalizedSelectedValue;

      groupElement.classList.toggle('hidden', !shouldShow);
      groupElement.classList.toggle('flex', shouldShow);

      if (shouldShow) {
        hasVisibleGroup = true;
        const defaultLensColorInput = groupElement.querySelector(
          '[data-lens-finder-lens-color-default="true"]',
        );
        let activeLensColorInput = null;

        if (defaultLensColorInput instanceof HTMLInputElement) {
          defaultLensColorInput.checked = true;
          activeLensColorInput = defaultLensColorInput;
        } else {
          const firstLensColorInput = groupElement.querySelector(
            'input[data-lens-finder-lens-color-choice]',
          );

          if (firstLensColorInput instanceof HTMLInputElement) {
            firstLensColorInput.checked = true;
            activeLensColorInput = firstLensColorInput;
          }
        }

        this.syncLensColorSelectValue(groupElement, activeLensColorInput?.value);
      }
    });

    this.lensColorsElement?.classList.toggle('hidden', !hasVisibleGroup);
  }

  syncLensColorSelectToRadio(groupElement, value) {
    const radio = Array.from(
      groupElement.querySelectorAll(
        'input[data-lens-finder-lens-color-choice]',
      ),
    ).find((input) => input.value === value);

    if (!(radio instanceof HTMLInputElement) || radio.checked) {
      return;
    }

    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  syncLensColorSelectValue(groupElement, value) {
    if (!value) {
      return;
    }

    const selectComponent = groupElement.querySelector('input-select');

    if (selectComponent && typeof selectComponent.setValue === 'function') {
      selectComponent.setValue(value);
    }
  }

  normalizeValue(value) {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  track(eventName, payload = {}) {
    window.LenszaAnalytics?.publish(eventName, payload);
  }

  trackStarted() {
    if (this.hasTrackedStart) {
      return;
    }

    if (typeof window.LenszaAnalytics?.publish !== 'function') {
      this.pendingStartTracking = true;
      return;
    }

    this.hasTrackedStart = true;
    this.pendingStartTracking = false;
    this.track('lens_finder_started');
  }

  handleAnalyticsReady() {
    if (this.pendingStartTracking) {
      this.trackStarted();
    }
  }

  getStepElementByIndex(stepIndex) {
    return this.stepElements[stepIndex - 1] || null;
  }
}

if (!customElements.get('lens-finder')) {
  customElements.define('lens-finder', LensFinder);
}
